import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProviderId } from '@trivio/contracts';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import {
  ApiProblemError,
  deleteProviderCredentials,
  getProviderBalance,
  listProviders,
  type ProviderBalance,
  type ProviderConnection,
} from '../../lib/api-client';
import { PROVIDER_CATALOG, ProviderConnectCard } from '../../components/ProviderConnectCard';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { providerIdPt } from '../../utils/enums-ptbr';
import { FrenetDepositSection } from './FrenetDepositSection';
import { useFrenetDeposit } from './useFrenetDeposit';

type ConnectionsState = Partial<Record<ProviderId, ProviderConnection>>;
type BalancesState = Partial<Record<ProviderId, ProviderBalance>>;

/** `/providers` (TTP-244) — reconnect/disconnect every provider, reusing onboarding's exact connect UI (`ProviderConnectCard.tsx`). */
export function ProvidersPage() {
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;

  const [connections, setConnections] = useState<ConnectionsState>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<ProviderId | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [balances, setBalances] = useState<BalancesState>({});
  const [balancesLoading, setBalancesLoading] = useState<Partial<Record<ProviderId, boolean>>>({});

  // TTP-270 — "Adicionar saldo" na Frenet. `?deposit=success|failure|pending`
  // is the exact query shape `frenet-deposit.service.ts`'s `CallbackUrls`
  // point back to (this issue's own decision, left open by the checklist as
  // "a decidir o formato exato") — read once on mount, then stripped from
  // the URL so a page refresh doesn't keep re-showing a stale banner.
  const [searchParams, setSearchParams] = useSearchParams();
  const depositReturnStatus = searchParams.get('deposit');
  const frenetDeposit = useFrenetDeposit();

  // `frenetDeposit.refreshLatest`/`setSearchParams` are deliberately absent
  // from these two dependency arrays — this project doesn't run the
  // `react-hooks` ESLint plugin (nothing enforces exhaustive-deps here), and
  // including them would defeat each effect's actual intent ("load once on
  // mount" / "react only to a real deposit query param change", not to a
  // function identity churning across renders).
  useEffect(() => {
    void frenetDeposit.refreshLatest();
  }, []);

  useEffect(() => {
    if (!depositReturnStatus) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('deposit');
      return next;
    });
  }, [depositReturnStatus]);

  /** No cache/TTL (v1, sem otimização prematura — TTP-267) — sempre uma consulta ao vivo por provedor conectado, disparada de novo a cada `load()`. Best-effort: uma falha aqui nunca bloqueia a tela nem o restante dos provedores. */
  const loadBalances = useCallback(
    (activeProviders: ProviderId[]) => {
      if (!apiKey) return;
      for (const provider of activeProviders) {
        setBalancesLoading((prev) => ({ ...prev, [provider]: true }));
        getProviderBalance(apiKey, provider)
          .then((balance) => {
            setBalances((prev) => ({ ...prev, [provider]: balance }));
          })
          .catch(() => {
            // Best-effort — o card simplesmente não mostra saldo para este provedor (mesmo estado de "ainda não buscado").
          })
          .finally(() => {
            setBalancesLoading((prev) => ({ ...prev, [provider]: false }));
          });
      }
    },
    [apiKey],
  );

  const load = useCallback(() => {
    if (!apiKey) return;
    setLoading(true);
    setError(null);
    listProviders(apiKey)
      .then((list) => {
        const next: ConnectionsState = {};
        for (const connection of list) {
          next[connection.provider] = connection;
        }
        setConnections(next);
        setBalances({});
        loadBalances(list.filter((connection) => connection.status === 'active').map((connection) => connection.provider));
      })
      .catch((err: unknown) => {
        setError(
          err instanceof ApiProblemError
            ? (err.problem.detail ?? err.problem.title)
            : 'Não foi possível carregar os provedores.',
        );
      })
      .finally(() => setLoading(false));
  }, [apiKey, loadBalances]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleConfirmDisconnect() {
    if (!apiKey || !disconnectTarget) return;
    setDisconnecting(true);
    setActionError(null);
    try {
      await deleteProviderCredentials(apiKey, disconnectTarget);
      setConnections((prev) => ({
        ...prev,
        [disconnectTarget]: {
          provider: disconnectTarget,
          status: 'disconnected',
          connected_at: prev[disconnectTarget]?.connected_at ?? '',
          last_validated_at: prev[disconnectTarget]?.last_validated_at ?? '',
        },
      }));
      // Nunca mostrar um saldo desatualizado para um provedor recém-desconectado.
      setBalances((prev) => {
        const next = { ...prev };
        delete next[disconnectTarget];
        return next;
      });
      setDisconnectTarget(null);
    } catch (err) {
      setActionError(
        err instanceof ApiProblemError
          ? (err.problem.detail ?? err.problem.title)
          : 'Não foi possível desconectar o provedor agora.',
      );
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Provedores</h1>
        <p className="text-sm text-muted-foreground">
          Conecte, reconecte ou desconecte suas próprias credenciais de cada provedor de frete.
        </p>
      </div>

      {actionError ? (
        <Alert variant="error" title="Ação não concluída">
          {actionError}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="error" title="Não foi possível carregar os provedores">
          {error}
        </Alert>
      ) : null}
      {depositReturnStatus === 'success' ? (
        <Alert variant="info" title="Pagamento confirmado">
          Depósito enviado ao Mercado Pago da Frenet. A liberação do saldo é assíncrona — pode levar alguns instantes
          para refletir aqui.
        </Alert>
      ) : null}
      {depositReturnStatus === 'pending' ? (
        <Alert variant="info" title="Pagamento em processamento">
          O Mercado Pago ainda está processando esse pagamento — acompanhe o status do depósito no card da Frenet.
        </Alert>
      ) : null}
      {depositReturnStatus === 'failure' ? (
        <Alert variant="error" title="Pagamento não concluído">
          O checkout do Mercado Pago não foi concluído — nenhum saldo foi adicionado. Você pode tentar novamente no
          card da Frenet.
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando provedores…" />
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {PROVIDER_CATALOG.map((provider) => {
            const providerBalance = balances[provider.id];
            return (
              <div key={provider.id} className="flex flex-col gap-4">
                <ProviderConnectCard
                  config={provider}
                  apiKey={apiKey ?? ''}
                  connection={connections[provider.id] ?? null}
                  showConnectionDetails
                  balanceLoading={balancesLoading[provider.id] ?? false}
                  onConnected={(connection) => {
                    setConnections((prev) => ({ ...prev, [provider.id]: connection }));
                    // Recém-(re)conectado — busca o saldo já nesta sessão, sem esperar um reload de página inteira.
                    if (connection.status === 'active') {
                      loadBalances([provider.id]);
                    }
                  }}
                  // Conditional spread instead of `prop={... : undefined}` —
                  // `exactOptionalPropertyTypes` rejects assigning `undefined`
                  // explicitly to an optional prop not typed with `| undefined`
                  // (same convention as api-client.ts's `request()`; see
                  // RuleEditor.tsx's setters for the sibling case).
                  {...(providerBalance !== undefined ? { balance: providerBalance } : {})}
                  {...(connections[provider.id]?.status === 'active'
                    ? { onDisconnect: () => setDisconnectTarget(provider.id) }
                    : {})}
                  disconnecting={disconnecting && disconnectTarget === provider.id}
                />
                {provider.id === 'frenet' && connections.frenet?.status === 'active' ? (
                  <FrenetDepositSection deposit={frenetDeposit} />
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={disconnectTarget !== null}
        title="Desconectar provedor"
        description={
          disconnectTarget
            ? `Tem certeza que deseja desconectar ${providerIdPt(disconnectTarget)}? A credencial guardada será removida e você precisará reconectar para voltar a cotar por este provedor.`
            : ''
        }
        confirmLabel="Desconectar"
        cancelLabel="Voltar"
        loading={disconnecting}
        onConfirm={handleConfirmDisconnect}
        onCancel={() => setDisconnectTarget(null)}
      />
    </div>
  );
}
